import { z } from 'zod';
import { inventoryUnitSchema } from '../units.js';
export const shoppingItemSchema = z.object({
    id: z.string().uuid(),
    foodId: z.string().uuid().nullable(),
    foodName: z.string().nullable(),
    freeText: z.string().nullable(),
    quantity: z.number().nullable(),
    unit: inventoryUnitSchema.nullable(),
    aisle: z.string().nullable(),
    checked: z.boolean(),
    addedToInventory: z.boolean(),
    note: z.string().nullable(),
    position: z.number().int(),
});
export const shoppingListSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    planId: z.string().uuid().nullable(),
    status: z.enum(['open', 'done', 'archived']),
    items: z.array(shoppingItemSchema),
});
export const shoppingListSummarySchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    status: z.enum(['open', 'done', 'archived']),
    itemCount: z.number().int(),
    checkedCount: z.number().int(),
});
export const createShoppingListSchema = z.object({
    name: z.string().min(1).max(80),
    /** Build the list from a week's plan, minus what is already in stock. */
    planId: z.string().uuid().nullish(),
    includeStaples: z.boolean().default(true),
});
export const addShoppingItemSchema = z
    .object({
    foodId: z.string().uuid().nullish(),
    freeText: z.string().min(1).max(200).nullish(),
    quantity: z.number().positive().nullish(),
    unit: inventoryUnitSchema.nullish(),
    aisle: z.string().max(60).nullish(),
    note: z.string().max(200).nullish(),
})
    .refine((v) => Boolean(v.foodId) || Boolean(v.freeText), {
    message: 'A shopping line needs either a food or some text',
});
/**
 * Something bought before that is not on this list yet: a food, or a free-text
 * line folded by lower-cased trimmed text. `lists` counts the lists it was on,
 * so one list with milk five times is still one; the quantity, unit and aisle
 * are from its most recent appearance.
 */
export const shoppingSuggestionSchema = z.object({
    foodId: z.string().uuid().nullable(),
    foodName: z.string().nullable(),
    freeText: z.string().nullable(),
    quantity: z.number().nullable(),
    unit: inventoryUnitSchema.nullable(),
    aisle: z.string().nullable(),
    lists: z.number().int(),
    lastAt: z.string().datetime(),
});
export const shoppingSuggestionsQuerySchema = z.object({
    limit: z.coerce.number().int().min(1).max(100).default(50),
});
export const checkShoppingItemSchema = z.object({ checked: z.boolean() });
export const addToInventorySchema = z.object({
    locationId: z.string().uuid(),
    /** Only the ticked lines, unless specific ids are given. */
    itemIds: z.array(z.string().uuid()).optional(),
    bestBefore: z.string().date().nullish(),
});
/**
 * A recipe's missing ingredients onto a list (M-90): what the house lacks
 * for the servings asked, netted against the inventory. Without a list id
 * the newest open list takes them, or a new one named Shopping.
 */
export const addRecipeToShoppingSchema = z.object({
    recipeId: z.string().uuid(),
    servings: z.number().positive().max(50).nullish(),
    listId: z.string().uuid().nullish(),
});
const shoppingLineNameSchema = z.object({
    name: z.string(),
    quantity: z.number().nullable(),
    unit: inventoryUnitSchema.nullable(),
});
export const addRecipeToShoppingResultSchema = z.object({
    listId: z.string().uuid(),
    listName: z.string(),
    /** Lines put on the list, with the shortfall. */
    added: z.array(shoppingLineNameSchema),
    /** Foods the house already has enough of. */
    inStock: z.array(z.string()),
    /** Foods already waiting on the list, unticked. */
    alreadyListed: z.array(z.string()),
    /** Lines with no matched food, put on as written because stock cannot be checked. */
    unchecked: z.array(z.string()),
    /** Sub-recipe lines, left for the cook to decide. */
    skipped: z.array(z.string()),
});
//# sourceMappingURL=shopping.js.map